import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { FaGoogle } from 'react-icons/fa';
import { FaUser, FaSignOutAlt, FaCreditCard } from 'react-icons/fa';
import LanguageSwitch from './LanguageSwitch';
import { trackLandingEvent } from '../services/analytics';

const Nav = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: 0.7rem 1.5rem;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(16px);
  z-index: 1000;
  border-bottom: 1px solid var(--color-border);
  box-shadow: 0 1px 0 rgba(15, 23, 42, 0.03);

  @media (max-width: 768px) {
    padding: 0.65rem 1rem;
  }
`;

const NavContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const StyledLogo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  flex: 0 0 auto;
`;

const LogoImage = styled.img`
  width: 28px;
  height: 28px;
`;

const LogoText = styled.span`
  font-family: var(--font-sans);
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text-primary);
  text-decoration: none;
  letter-spacing: 0;
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;

  @media (max-width: 860px) {
    gap: 0.65rem;
    overflow-x: auto;
    padding-bottom: 2px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media (max-width: 640px) {
    .mobile-hidden {
      display: none;
    }
  }
`;

const NavLink = styled(Link)`
  color: var(--color-text-secondary);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.92rem;
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-text-primary);
  }
`;

const NavButton = styled.button`
  color: var(--color-text-secondary);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.92rem;
  transition: color 0.2s ease;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 0;

  &:hover {
    color: var(--color-text-primary);
  }
`;

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1rem;
  background: var(--color-accent-soft);
  border: 1px solid transparent;
  border-radius: 999px;
  color: var(--color-text-primary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background-color: rgba(30, 64, 175, 0.16);
    transform: translateY(-1px);
  }

  svg {
    font-size: 18px;
    color: var(--color-accent-dark);
  }

  @media (max-width: 640px) {
    padding: 0.55rem 0.65rem;

    .google-label {
      display: none;
    }
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: var(--color-surface);
  border-radius: var(--radius-small);
  box-shadow: var(--shadow-soft);
  min-width: 160px;
  z-index: 1000;
  display: ${props => props.$isOpen ? 'block' : 'none'};
`;

const UserWelcome = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--color-border);
  font-size: 14px;
  color: var(--color-text-secondary);

  .welcome {
    color: var(--color-text-muted);
    font-size: 12px;
    margin-bottom: 2px;
  }

  .name {
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const UserMenu = styled.div`
  position: relative;
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  overflow: hidden;
  background-color: var(--color-surface-alt);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  svg {
    width: 20px;
    height: 20px;
    color: var(--color-text-muted);
  }

  &.fallback {
    svg {
      display: block;
    }
  }
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  color: var(--color-text-secondary);
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: var(--color-surface-alt);
  }

  svg {
    font-size: 16px;
    color: var(--color-text-muted);
  }

  &:last-child {
    border-top: 1px solid var(--color-border);
  }
`;

const Navbar: React.FC = () => {
  const { t } = useLanguage();
  const { user, signInWithGoogle, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [avatarError, setAvatarError] = useState(false);

  // 当路径改变时关闭下拉菜单
  useEffect(() => {
    setIsDropdownOpen(false);
  }, [location.pathname]);

  // 点击页面其他地方时关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isDropdownOpen && !target.closest('.user-menu')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // 当用户变化时重置头像错误状态
  useEffect(() => {
    if (user) {
      setAvatarError(false);
    }
  }, [user]);

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate(`/#${sectionId}`);
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLogout = async () => {
    trackLandingEvent('logout_click', { placement: 'navbar_menu' });
    await logout();
    setIsDropdownOpen(false);
  };

  const handleSignIn = async () => {
    trackLandingEvent('login_start', { placement: 'navbar' });
    try {
      await signInWithGoogle();
      trackLandingEvent('login_success', { placement: 'navbar' });
    } catch (error) {
      trackLandingEvent('login_failed', { placement: 'navbar' });
      console.error('Navbar login failed:', error);
    }
  };

  return (
    <Nav>
      <NavContainer>
        <StyledLogo to="/" onClick={() => trackLandingEvent('nav_click', { target: 'home_logo' })}>
          <LogoImage src="/icon.png" alt="SheetMate Logo" />
          <LogoText>SheetMate</LogoText>
        </StyledLogo>
        <NavLinks>
          <NavButton
            className="mobile-hidden"
            onClick={() => {
              if (location.pathname !== '/') {
                trackLandingEvent('nav_click', { target: 'home' });
                navigate('/');
              } else {
                trackLandingEvent('nav_click', { target: 'hero' });
                scrollToSection('hero');
              }
            }}
          >
            {t('navigation.home')}
          </NavButton>
          <NavButton
            className="mobile-hidden"
            onClick={() => {
              trackLandingEvent('nav_click', { target: 'features' });
              scrollToSection('features');
            }}
          >
            {t('navigation.features')}
          </NavButton>
          <NavButton
            className="mobile-hidden"
            onClick={() => {
              trackLandingEvent('nav_click', { target: 'faq' });
              scrollToSection('faq');
            }}
          >
            {t('navigation.faq')}
          </NavButton>
          <NavLink className="mobile-hidden" to="/solutions" onClick={() => trackLandingEvent('nav_click', { target: 'solutions' })}>{t('navigation.solutions')}</NavLink>
          <NavLink to="/pricing" onClick={() => trackLandingEvent('nav_click', { target: 'pricing' })}>{t('navigation.pricing')}</NavLink>
          <LanguageSwitch />
          {user ? (
            <UserMenu className="user-menu">
              <UserAvatar
                className={(!user.photoURL || avatarError) ? 'fallback' : ''}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                {user.photoURL && !avatarError ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || 'User'}
                    onError={() => setAvatarError(true)}
                    referrerPolicy="no-referrer"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <FaUser />
                )}
              </UserAvatar>
              <DropdownMenu $isOpen={isDropdownOpen}>
                <UserWelcome>
                  <div className="welcome">{t('navigation.welcome')}</div>
                  <div className="name">{user.displayName || user.email}</div>
                </UserWelcome>
                <DropdownItem
                  onClick={() => {
                    setIsDropdownOpen(false);
                    trackLandingEvent('nav_click', { target: 'my_subscription' });
                    navigate('/mySubscription');
                  }}
                >
                  <FaCreditCard />
                  {t('navigation.mySubscription')}
                </DropdownItem>
                <DropdownItem onClick={handleLogout}>
                  <FaSignOutAlt />
                  {t('navigation.signOut')}
                </DropdownItem>
              </DropdownMenu>
            </UserMenu>
          ) : (
            <GoogleButton onClick={handleSignIn} aria-label={t('navigation.signInWithGoogle')}>
              <FaGoogle />
              <span className="google-label">{t('navigation.signInWithGoogle')}</span>
            </GoogleButton>
          )}
        </NavLinks>
      </NavContainer>
    </Nav>
  );
};

export default Navbar;
